import type {
  NarrativeRule,
  ObjectCategory,
  RecurringObject,
  SensorySignature,
  SignatureScope,
  StoryElements,
  StoryElementsStats
} from '@/types';

export const STORY_ELEMENTS_STORAGE_KEY = 'novelwriter.storyElements';

export const objectCategoryLabels: Record<ObjectCategory, string> = {
  weapon: 'Weapon',
  keepsake: 'Keepsake',
  tool: 'Tool',
  clothing: 'Clothing',
  vehicle: 'Vehicle',
  document: 'Document',
  other: 'Other'
};

export const objectCategoryIcons: Record<ObjectCategory, string> = {
  weapon: '⚔️',
  keepsake: '🪙',
  tool: '🗝️',
  clothing: '🧥',
  vehicle: '🚗',
  document: '📜',
  other: '📦'
};

export const scopeLabels: Record<SignatureScope, string> = {
  location: 'Location',
  character: 'Character',
  act: 'Act',
  global: 'Global'
};

export const signatureFrequencyLabels = {
  always: 'Always',
  usually: 'Usually',
  sometimes: 'Sometimes',
  rarely: 'Rarely'
} as const;

export const ruleSeverityLabels = {
  'hard-rule': 'Hard Rule',
  'strong-guideline': 'Strong Guideline',
  'soft-guideline': 'Soft Guideline'
} as const;

export const ruleScopeLabels = {
  'all-chapters': 'Entire Manuscript',
  'specific-acts': 'Specific Acts',
  'specific-characters': 'Specific Characters'
} as const;

export const formatDateISO = (value: string): string => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '—';
  }
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const createEmptyStoryElements = (manuscriptId: string): StoryElements => {
  const now = new Date().toISOString();
  return {
    id: generateId('story-elements'),
    manuscriptId,
    recurringObjects: [],
    sensorySignatures: [],
    narrativeRules: [],
    uiState: { lastActiveTab: 'objects' },
    createdAt: now,
    updatedAt: now,
    notes: ''
  };
};

export const calculateStats = (storyElements: StoryElements): StoryElementsStats => {
  const criticalObjects = storyElements.recurringObjects.filter((object) => object.importance === 'critical').length;
  const hardRules = storyElements.narrativeRules.filter((rule) => rule.severity === 'hard-rule').length;

  return {
    totalObjects: storyElements.recurringObjects.length,
    criticalObjects,
    totalSignatures: storyElements.sensorySignatures.length,
    totalRules: storyElements.narrativeRules.length,
    hardRules
  };
};

export const normalizeRecurringObject = (object: RecurringObject): RecurringObject => ({
  ...object,
  firstAppearance: object.firstAppearance ?? '',
  lastSeen: object.lastSeen ?? '',
  currentLocation: object.currentLocation ?? ''
});

export const normalizeSignature = (signature: SensorySignature): SensorySignature => ({
  ...signature,
  scopeId: signature.scopeId ?? undefined,
  scopeName: signature.scopeName ?? undefined
});

export const normalizeRule = (rule: NarrativeRule): NarrativeRule => ({
  ...rule,
  actIds: rule.actIds ?? [],
  characterIds: rule.characterIds ?? []
});

export const generateId = (prefix: string): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const random = Math.random().toString(36).slice(2, 10);
  return `${prefix}-${random}`;
};
